import { LLM_MODEL_PRICING } from './llm-pricing';

/**
 * PD-17582: features ask for a ROLE, not a model name, so moving a feature to
 * a better model is one setting rather than a hunt through hard-coded ids.
 *
 * - `writing`: text a person will read (campaign emails, call scripts).
 * - `reading`: reading and sorting what comes back (campaign replies).
 *
 * Only the campaign features read these roles for now. Workd's existing AI
 * features (agents, email ingest, product lookups, call analysis,
 * translations) keep their own models until they are moved onto roles as a
 * separate piece of work, after the AI agent work in progress (owner,
 * 10/26/26), which also adds the agent and judging roles.
 *
 * Workd associates change a role's model in config-web (AI Models, next to
 * AI Usage and the AI Rate Card); crm-api reads it through config-api
 * (`AiModelRoleService`). These defaults apply when a role has no saved
 * choice, and whenever the saved choice cannot be read.
 */
export type AiModelRole = 'writing' | 'reading';

export const AI_MODEL_ROLES: ReadonlyArray<AiModelRole> = ['writing', 'reading'];

/** Owner decision (10/26/26): Claude Haiku 5.5 runs everything campaigns do. */
export const AI_MODEL_ROLE_DEFAULTS: Readonly<Record<AiModelRole, string>> = {
	writing: 'claude-haiku-5-5',
	reading: 'claude-haiku-5-5',
};

export const AI_MODEL_ROLE_LABELS: Readonly<Record<AiModelRole, string>> = {
	writing: 'Writing',
	reading: 'Reading',
};

export const AI_MODEL_ROLE_DESCRIPTIONS: Readonly<Record<AiModelRole, string>> =
	{
		writing: 'Text a person reads: campaign emails and call scripts.',
		reading: 'Reading and sorting campaign replies.',
	};

/**
 * The models a role may be set to. Claude only: every role's call path is
 * built on the Claude API, and each id here is priced in `LLM_MODEL_COSTS`
 * (ai-models.test.ts checks it) so usage is never recorded at no cost.
 */
export const AI_MODEL_ROLE_CHOICES: ReadonlyArray<string> = [
	'claude-haiku-5-5',
	'claude-sonnet-5-5',
	'claude-opus-5-5',
];

/** Display names for the role choices. */
export const AI_MODEL_ROLE_CHOICE_LABELS: Readonly<Record<string, string>> = {
	'claude-haiku-5-5': 'Claude Haiku 5.5',
	'claude-sonnet-5-5': 'Claude Sonnet 5.5',
	'claude-opus-5-5': 'Claude Opus 5.5',
};

/**
 * Redis key, on the SHARED cross-app database, of crm-api's cached copy of
 * the saved role choices. Global: the roles are the same for every tenant.
 * config-api deletes it when a role is saved, so crm-api reloads on its next
 * refresh (the AI rate card pattern).
 */
export const AI_MODEL_ROLES_CACHE_KEY = 'ai-model-roles:published';

export function isAiModelRole(value: unknown): value is AiModelRole {
	return (
		typeof value === 'string' &&
		(AI_MODEL_ROLES as ReadonlyArray<string>).includes(value)
	);
}

/**
 * The model a role runs on: its saved choice when that is still a model a
 * role may be set to, else the role's default. A saved choice that has since
 * left `AI_MODEL_ROLE_CHOICES` falls back rather than reaching the API.
 */
export function resolveAiModelForRole(
	role: AiModelRole,
	saved: Partial<Record<AiModelRole, string | null>> | null | undefined,
): string {
	const choice = saved?.[role]?.trim();

	if (choice != null && AI_MODEL_ROLE_CHOICES.includes(choice)) {
		return choice;
	}

	return AI_MODEL_ROLE_DEFAULTS[role];
}

/**
 * The Claude model the agent LLM provider runs when nothing names one, or when
 * the named model is retired or is not a Claude model (PD-16962). It is the
 * Claude provider's own fallback and the factory's Claude default, kept in one
 * place so they cannot drift apart again (the id used to be repeated in four
 * places). It does not choose the platform's engine: that is `PLATFORM_LLM`.
 */
export const DEFAULT_CLAUDE_MODEL = 'claude-sonnet-5';

/**
 * The LLM models the platform supports, in one place (PD-16759).
 *
 * The provider cost table (`LLM_MODEL_COSTS`) must cover this list: a
 * supported model with no cost entry records a NULL cost, so
 * `llm-pricing.test.ts` checks every id here.
 *
 * `AGENT_LLM_MODELS` is the list crm-api's `LLMFactoryService.getProviderInfo`
 * declares for the conversation agents; it moved here unchanged so the
 * cost coverage test sees the same list the factory serves.
 *
 * PD-16962: the Claude list holds only models the API still serves. The
 * Claude 3 family and Claude Sonnet 4 / Opus 4 (`-20250514`) are retired and
 * answer 404, so they are not offered; the provider maps a stored id of a
 * retired model to `DEFAULT_CLAUDE_MODEL` instead of failing the request.
 * Their cost entries stay in `LLM_MODEL_COSTS` so historical costs remain
 * recomputable.
 *
 * PD-16964: this is also the list Workd associates choose an agent's chat and
 * voice model from (constants/agent-llm.ts). An agent whose stored model is
 * removed from it falls back (voice to chat, chat to the platform default)
 * and its conversations log a warning until another model is chosen.
 */
export const AGENT_LLM_MODELS: Readonly<{
	claude: ReadonlyArray<string>;
	openai: ReadonlyArray<string>;
}> = {
	claude: [
		'claude-haiku-5-5',
		'claude-sonnet-5-5',
		'claude-opus-5-5',
		'claude-sonnet-5',
		'claude-opus-4-8',
		'claude-haiku-4-5-20251001',
	],
	// gpt-6-luna (PD-14427): an evaluation candidate; it takes tools only at
	// reasoning effort 'none' on Chat Completions (openai-model-capability).
	openai: ['gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano', 'gpt-6-luna'],
};

/**
 * Every model id the platform may call: the agent models plus the models
 * the background pipelines may be configured to run. May contain a dated
 * snapshot and its alias side by side (`claude-haiku-4-5-20251001` and
 * `claude-haiku-4-5`).
 */
export const SUPPORTED_LLM_MODEL_IDS: ReadonlyArray<string> = Array.from(
	new Set([
		...AGENT_LLM_MODELS.claude,
		...AGENT_LLM_MODELS.openai,
		...LLM_MODEL_PRICING.map((entry) => entry.modelId),
	]),
);
